import { create } from "zustand";
import {
  loginAdmin,
  updateAdminCredentials,
  createFAQ,
  updateFAQ,
  deleteFAQ as deleteFAQAPI,
  createNotice,
  updateNotice,
  deleteNotice as deleteNoticeAPI,
  getUserStats,
  fetchUsers,
  deleteUser,
} from "../utils/api";

// 공지사항 스토어
export const useNoticeStore = create((set, get) => ({
  notices: [],
  currentPage: 1,
  itemsPerPage: 10,
  sortBy: "recent",
  searchTerm: "",
  isLoading: false,
  error: null,

  setCurrentPage: (page) => set({ currentPage: page }),
  setSortBy: (sort) => set({ sortBy: sort }),
  setSearchTerm: (term) => set({ searchTerm: term }),
  clearError: () => set({ error: null }),

  // 공지사항 추가
  addNotice: async (noticeData) => {
    const { token } = useAuthStore.getState();
    set({ isLoading: true, error: null });

    try {
      const response = await createNotice(noticeData, token);

      if (response.success) {
        set((state) => ({
          notices: [response.data, ...state.notices],
          isLoading: false,
        }));
        return { success: true };
      }
    } catch (error) {
      // 토큰 무효 시 자동 로그아웃 처리
      if (error.status === 401 || /token/i.test(error.message)) {
        useAuthStore.getState().logout();
      }
      set({ isLoading: false, error: error.message });
      return { success: false, error: error.message };
    }
  },

  // 공지사항 수정
  updateNotice: async (id, updateData) => {
    const { token } = useAuthStore.getState();
    set({ isLoading: true, error: null });

    try {
      const response = await updateNotice(id, updateData, token);

      if (response.success) {
        set((state) => ({
          notices: state.notices.map((notice) =>
            notice.id === id ? response.data : notice
          ),
          isLoading: false,
        }));
        return { success: true };
      }
    } catch (error) {
      if (error.status === 401 || /token/i.test(error.message)) {
        useAuthStore.getState().logout();
      }
      set({ isLoading: false, error: error.message });
      return { success: false, error: error.message };
    }
  },

  // 공지사항 삭제
  deleteNotice: async (id) => {
    const { token } = useAuthStore.getState();
    set({ isLoading: true, error: null });

    try {
      const response = await deleteNoticeAPI(id, token);

      if (response.success) {
        set((state) => ({
          notices: state.notices.filter((notice) => notice.id !== id),
          isLoading: false,
        }));
        return { success: true };
      }
    } catch (error) {
      if (error.status === 401 || /token/i.test(error.message)) {
        useAuthStore.getState().logout();
      }
      set({ isLoading: false, error: error.message });
      return { success: false, error: error.message };
    }
  },

  // 필터링된 공지사항 목록
  getFilteredNotices: () => {
    const { notices, searchTerm, sortBy } = get();
    let filtered = notices;

    if (searchTerm) {
      filtered = filtered.filter(
        (notice) =>
          (notice.title &&
            notice.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (notice.body &&
            notice.body.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    filtered.sort((a, b) => {
      if (sortBy === "recent")
        return new Date(b.createdAt) - new Date(a.createdAt);
      if (sortBy === "old")
        return new Date(a.createdAt) - new Date(b.createdAt);
      return 0;
    });

    return filtered;
  },
}));

// FAQ 스토어
export const useFAQStore = create((set, get) => ({
  faqs: [],
  currentPage: 1,
  itemsPerPage: 10,
  sortBy: "recent",
  searchTerm: "",
  isLoading: false,
  error: null,

  setCurrentPage: (page) => set({ currentPage: page }),
  setSortBy: (sort) => set({ sortBy: sort }),
  setSearchTerm: (term) => set({ searchTerm: term }),
  clearError: () => set({ error: null }),

  // FAQ 추가
  addFAQ: async (faqData) => {
    const { token } = useAuthStore.getState();
    set({ isLoading: true, error: null });

    try {
      const response = await createFAQ(faqData, token);

      if (response.success) {
        set((state) => ({
          faqs: [response.data, ...state.faqs],
          isLoading: false,
        }));
        return { success: true };
      }
    } catch (error) {
      if (error.status === 401 || /token/i.test(error.message)) {
        useAuthStore.getState().logout();
      }
      set({ isLoading: false, error: error.message });
      return { success: false, error: error.message };
    }
  },

  // FAQ 수정
  updateFAQ: async (id, updateData) => {
    const { token } = useAuthStore.getState();
    set({ isLoading: true, error: null });

    try {
      const response = await updateFAQ(id, updateData, token);

      if (response.success) {
        set((state) => ({
          faqs: state.faqs.map((faq) => (faq.id === id ? response.data : faq)),
          isLoading: false,
        }));
        return { success: true };
      }
    } catch (error) {
      set({
        isLoading: false,
        error: error.message,
      });
      return { success: false, error: error.message };
    }
  },

  // FAQ 삭제
  deleteFAQ: async (id) => {
    const { token } = useAuthStore.getState();
    set({ isLoading: true, error: null });

    try {
      const response = await deleteFAQAPI(id, token);

      if (response.success) {
        set((state) => ({
          faqs: state.faqs.filter((faq) => faq.id !== id),
          isLoading: false,
        }));
        return { success: true };
      }
    } catch (error) {
      set({
        isLoading: false,
        error: error.message,
      });
      return { success: false, error: error.message };
    }
  },

  // 필터링된 FAQ 목록
  getFilteredFAQs: () => {
    const { faqs, searchTerm, sortBy } = get();
    let filtered = faqs;

    if (searchTerm) {
      filtered = filtered.filter(
        (faq) =>
          (faq.title &&
            faq.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (faq.body &&
            faq.body.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    filtered.sort((a, b) => {
      if (sortBy === "recent")
        return new Date(b.createdAt) - new Date(a.createdAt);
      if (sortBy === "old")
        return new Date(a.createdAt) - new Date(b.createdAt);
      return 0;
    });

    return filtered;
  },
}));

// 회원 스토어 (서버 연동)
export const useMemberStore = create((set, get) => ({
  members: [],
  total: 0,
  nextCursor: null,
  cursorMap: { 1: null }, // 페이지 -> startAfter 커서
  currentPage: 1,
  itemsPerPage: 10,
  sortBy: "recent",
  searchTerm: "",
  searchField: "email", // 기본 검색 필드 ("name" 또는 "email")
  isLoading: false,
  error: null,

  setCurrentPage: (page) => set({ currentPage: page }),
  setSortBy: (sort) => set({ sortBy: sort }),
  setSearchTerm: (term) => set({ searchTerm: term }),
  setSearchField: (field) => set({ searchField: field }),
  clearError: () => set({ error: null }),

  // 서버에서 회원 목록 조회
  fetchMembers: async (targetPage = 1) => {
    const state = get();
    const token = useAuthStore.getState().token;

    set({ isLoading: true, error: null });

    try {
      // 필요한 커서 확보 (cursorMap에 없으면 앞에서부터 순차 로드)
      let cursorMap = { ...state.cursorMap };
      let pageToLoad = 1;
      let startAfter = null;

      if (cursorMap[targetPage] === undefined) {
        // 가장 가까운 이전 페이지 커서를 찾음
        const pages = Object.keys(cursorMap)
          .map((p) => parseInt(p, 10))
          .sort((a, b) => a - b);
        const maxKnown = pages[pages.length - 1] || 1;
        pageToLoad = Math.max(1, maxKnown);
        startAfter = cursorMap[pageToLoad] || null;

        // targetPage까지 커서만 채우기
        while (pageToLoad < targetPage) {
          const resp = await fetchUsers(
            {
              limit: state.itemsPerPage,
              startAfter: startAfter || undefined,
              search: state.searchTerm || undefined,
            },
            token
          );
          cursorMap[pageToLoad + 1] = resp.nextCursor || null;
          startAfter = resp.nextCursor || null;
          pageToLoad += 1;
        }
      } else {
        startAfter = cursorMap[targetPage];
      }

      // 실제 targetPage 데이터 로드
      const response = await fetchUsers(
        {
          limit: state.itemsPerPage,
          startAfter: startAfter || undefined,
          search: state.searchTerm || undefined,
          searchField: state.searchTerm ? state.searchField : undefined,
        },
        token
      );

      // 다음 페이지 커서 갱신
      cursorMap[targetPage + 1] = response.nextCursor || null;

      set({
        members: (response.users || []).map((u) => ({
          id: u.id,
          phoneNumber: u.phoneNumber,
          joinedAt: u.createdAt,
        })),
        total: response.total || 0,
        nextCursor: response.nextCursor || null,
        cursorMap,
        currentPage: targetPage,
        isLoading: false,
      });

      return { success: true };
    } catch (error) {
      if (error.status === 401 || /token/i.test(error.message)) {
        useAuthStore.getState().logout();
      }
      set({ isLoading: false, error: error.message });
      return { success: false, error: error.message };
    }
  },

  // 유저 삭제 (소프트 삭제)
  deleteMember: async (userId) => {
    const token = useAuthStore.getState().token;
    set({ isLoading: true, error: null });

    try {
      await deleteUser(userId, token); // 204 No Content 기대
      // 현재 목록에서 제거
      set((state) => ({
        members: state.members.filter((m) => m.id !== userId),
        total: Math.max(0, (state.total || 1) - 1),
        isLoading: false,
      }));
      return { success: true };
    } catch (error) {
      set({ isLoading: false, error: error.message });
      return { success: false, error: error.message };
    }
  },

  // 클라이언트 정렬/검색은 서버 결과에 보조적으로만 사용 (현재는 그대로 반환)
  getFilteredMembers: () => {
    const { members } = get();
    return members;
  },
}));

// 인증 스토어
export const useAuthStore = create((set, get) => ({
  // 인증 상태
  admin: null,
  token: localStorage.getItem("adminToken"),
  isAuthenticated: false,
  isLoading: false,
  error: null,

  // 로그인
  login: async (email, password) => {
    set({ isLoading: true, error: null });

    try {
      const data = await loginAdmin(email, password);

      if (data.success) {
        const { admin, token } = data.data;

        // 로컬스토리지에 토큰 저장
        localStorage.setItem("adminToken", token);
        localStorage.setItem("adminData", JSON.stringify(admin));

        set({
          admin,
          token,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });

        return { success: true };
      }
    } catch (error) {
      set({
        isLoading: false,
        error: error.message,
        isAuthenticated: false,
      });
      return { success: false, error: error.message };
    }
  },

  // 로그아웃
  logout: () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminData");
    set({
      admin: null,
      token: null,
      isAuthenticated: false,
      error: null,
    });
  },

  // 자동 로그인 (토큰 검증)
  checkAuth: () => {
    const token = localStorage.getItem("adminToken");
    const adminData = localStorage.getItem("adminData");

    if (token && adminData) {
      try {
        const admin = JSON.parse(adminData);
        set({
          admin,
          token,
          isAuthenticated: true,
        });
      } catch (error) {
        localStorage.removeItem("adminToken");
        localStorage.removeItem("adminData");
        set({
          admin: null,
          token: null,
          isAuthenticated: false,
        });
      }
    }
  },

  // 관리자 정보 업데이트
  updateAdminInfo: async (updateData) => {
    const { token } = get();
    set({ isLoading: true, error: null });

    try {
      const data = await updateAdminCredentials(updateData, token);

      if (data.success) {
        const updatedAdmin = data.data;

        // 로컬스토리지 업데이트
        localStorage.setItem("adminData", JSON.stringify(updatedAdmin));

        set({
          admin: updatedAdmin,
          isLoading: false,
          error: null,
        });

        return { success: true };
      }
    } catch (error) {
      set({
        isLoading: false,
        error: error.message,
      });
      return { success: false, error: error.message };
    }
  },

  // 에러 초기화
  clearError: () => set({ error: null }),
}));

// 대시보드 스토어
export const useDashboardStore = create((set, get) => ({
  stats: {
    totalUsers: 0,
    newUsersToday: 0,
    withdrawnUsersToday: 0,
  },
  isLoading: false,
  error: null,

  // 사용자 통계 조회
  fetchUserStats: async () => {
    const { token } = useAuthStore.getState();
    set({ isLoading: true, error: null });

    try {
      const response = await getUserStats(token);

      if (response.success) {
        set({
          stats: response.data,
          isLoading: false,
        });
        return { success: true };
      }
    } catch (error) {
      set({
        isLoading: false,
        error: error.message,
      });
      return { success: false, error: error.message };
    }
  },

  // 에러 초기화
  clearError: () => set({ error: null }),
}));
